// src/shared/api/inventoryService.js
import axios from "axios";

/**
 * Inventory item CRUD (admin) + Inventory usage logs
 *
 * Items CRUD:           /api/admin/inventory
 * Usage list/create:    /api/public/batches/{batchId}/inventory-usage
 * Usage delete:         /api/inventory-usage/{id}
 *
 * NEW Lookups (dynamic dropdowns):
 *   GET  /api/admin/inventory/lookups
 *   POST /api/admin/inventory/lookups/{key}   body: { value: "KG" }
 */

const BASE =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:8080";

const ITEMS_BASE = `${BASE}/api/inventory`;
const USAGE_BATCH_BASE = `${BASE}/api/public/batches`;
const USAGE_DELETE_BASE = `${BASE}/api/inventory-usage`;

const createClient = (baseURL) => {
  const token = localStorage.getItem('authToken');
  return axios.create({
    baseURL,
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
      'Content-Type': 'application/json',
    },
  });
};

/* ---------------- LOOKUPS (Dynamic Dropdowns) ----------------
   If backend URL differs, edit these 2 constants only. */
const LOOKUPS_BASE = `${BASE}/api/admin/inventory/lookups`; // GET returns { itemTypes:[], units:[], usageReasons:[] }
const LOOKUP_ADD = (key) => `${LOOKUPS_BASE}/${encodeURIComponent(key)}`; // POST { value }

const defaultLookups = () => ({
  itemTypes: ['FEED', 'VACCINE', 'MEDICINE', 'SUPPLEMENT', 'OTHER'],
  units: ['KG', 'LITRE', 'UNIT'],
  usageReasons: ['Feeding', 'Medicine', 'Cleaning', 'Other'],
});

const inventoryService = {
  // ----- Items (admin) -----
  getAllInventory: async () => {
    const { data } = await createClient(ITEMS_BASE).get('');
    return data;
  },
  addInventory: async (inventoryData) => {
    const { data } = await createClient(ITEMS_BASE).post('', inventoryData);
    return data;
  },
  updateInventory: async (id, inventoryData) => {
    const { data } = await createClient(ITEMS_BASE).put(`/${id}`, inventoryData);
    return data;
  },
  restockInventory: async (id, quantity, costPerUnit, expiryDate = null) => {
    const { data } = await createClient(ITEMS_BASE).patch(`/${id}/restock`, { quantity, costPerUnit, expiryDate });
    return data;
  },
  getInventoryHistory: async (id) => {
    const { data } = await createClient(ITEMS_BASE).get(`/${id}/history`);
    return data;
  },
  getDeleteImpact: async (id) => {
    const { data } = await createClient(ITEMS_BASE).get(`/${id}/delete-impact`);
    return data;
  },
  deleteInventory: async (id, cascade = false) => {
    await createClient(ITEMS_BASE).delete(`/${id}`, { params: { cascade } });
  },

  // ----- Usage (preferred) -----
  /** GET /api/public/batches/{batchId}/inventory-usage */
  getUsageByBatch: async (batchId) => {
    const { data } = await createClient(`${USAGE_BATCH_BASE}/${batchId}`).get('/inventory-usage');
    return data;
  },

  /** POST /api/public/batches/{batchId}/inventory-usage { inventoryId, quantityUsed, reason, taskId? } */
  createUsage: async (batchId, usage) => {
    const { data } = await createClient(`${USAGE_BATCH_BASE}/${batchId}`).post('/inventory-usage', usage);
    return data;
  },

  /** DELETE /api/inventory-usage/{id} */
  deleteUsage: async (usageId) => {
    await createClient(USAGE_DELETE_BASE).delete(`/${usageId}`);
  },

  // (Backward-compat) legacy endpoint you already had:
  useInventory: async (usageData) => {
    const { data } = await createClient(ITEMS_BASE).post('/use', usageData);
    return data;
  },

  // ----- NEW: Lookups -----
  /**
   * Returns { itemTypes:[], units:[], usageReasons:[] }
   * Falls back to defaults if backend not ready.
   */
  getLookups: async () => {
    try {
      const { data } = await createClient(LOOKUPS_BASE).get('');
      // tolerate different shapes
      const itemTypes = data?.itemTypes || data?.types || data?.ITEM_TYPES || [];
      const units = data?.units || data?.UNITS || [];
      const usageReasons = data?.usageReasons || data?.reasons || data?.USAGE_REASONS || [];
      const fallback = defaultLookups();
      return {
        itemTypes: Array.isArray(itemTypes) && itemTypes.length ? itemTypes : fallback.itemTypes,
        units: Array.isArray(units) && units.length ? units : fallback.units,
        usageReasons: Array.isArray(usageReasons) && usageReasons.length ? usageReasons : fallback.usageReasons,
      };
    } catch (e) {
      // backend may not have it yet
      return defaultLookups();
    }
  },

  /**
   * key: "itemTypes" | "units" | "usageReasons"  (you can decide backend keys)
   * value: new option string
   */
  addLookupOption: async (key, value) => {
    const cleanValue = String(value || '').trim();
    if (!cleanValue) throw new Error('Empty value');
    const { data } = await createClient(LOOKUP_ADD(key)).post('', { value: cleanValue });
    return data;
  },
};

export default inventoryService;
